/**
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at

 * http://www.apache.org/licenses/LICENSE-2.0

 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// biome-ignore lint/suspicious/noExplicitAny: This recursive utility accepts arbitrary object shapes.
export function merge(target: any, source: any): void {
  if (!isObject(target) && !isObject(source)) {
    return
  }
  for (const key in source) {
    // biome-ignore lint/suspicious/noPrototypeBuiltins: Object.hasOwn is unavailable for the ES5 target.
    if (Object.prototype.hasOwnProperty.call(source, key) as boolean) {
      const targetProp = target[key]
      const sourceProp = source[key]
      if (isObject(sourceProp) && isObject(targetProp)) {
        merge(targetProp, sourceProp)
      } else {
        target[key] = clone(sourceProp)
      }
    }
  }
}

export function clone<T>(target: T): T {
  if (!isObject(target)) {
    return target
  }

  // biome-ignore lint/suspicious/noExplicitAny: The cloned container is populated dynamically.
  let copy: any = null
  if (isArray(target)) {
    copy = []
  } else {
    copy = {}
  }
  for (const key in target) {
    // biome-ignore lint/suspicious/noPrototypeBuiltins: Object.hasOwn is unavailable for the ES5 target.
    if (Object.prototype.hasOwnProperty.call(target, key) as boolean) {
      const v = target[key]
      if (isObject(v)) {
        copy[key] = clone(v)
      } else {
        copy[key] = v
      }
    }
  }
  return copy
}

export function isArray<T = unknown>(value: unknown): value is T[] {
  return Object.prototype.toString.call(value) === '[object Array]'
}

export function isFunction<T = (...args: unknown[]) => unknown>(value: unknown): value is T {
  return typeof value === 'function'
}

export function isObject(value: unknown): value is object {
  return typeof value === 'object' && isValid(value)
}

export function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

export function isValid<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}

export function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean'
}

export function isString(value: unknown): value is string {
  return typeof value === 'string'
}
